import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import $ from "jquery";
import "datatables.net-dt/js/dataTables.dataTables.js";
import "datatables.net-dt/css/dataTables.dataTables.css";
import axios from "axios";
import Swal from "sweetalert2";
import { createMRTColumnHelper } from "material-react-table";
import { Box, Chip } from "@mui/material";
import { baseUrl } from "../../../../helper/urls";
import AdminCustomTable from "../../../../components/custom_table/AdminCustomTable";
import { useSelector } from "react-redux";

const AllStudent = () => {
  const { token } = useSelector((state) => state.auth);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const navigate = useNavigate();

  const formatDateToLocalYYYYMMDD = (date) => {
    if (!date) return "";
    const d = new Date(date); // safe if date is already a string
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const fetchStudents = async (startDateParam = null, endDateParam = null) => {
    try {
      const params = new URLSearchParams();

      if (startDateParam) {
        params.append("startDate", formatDateToLocalYYYYMMDD(startDateParam));
      }
      if (endDateParam) {
        params.append("endDate", formatDateToLocalYYYYMMDD(endDateParam));
      }

      const queryString = params.toString();
      const apiUrl = queryString
        ? `${baseUrl}/api/students/all?${queryString}`
        : `${baseUrl}/api/students/all`;

      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.get(apiUrl, {
        headers,
      });

      setStudents(response.data.data || []);
      setLoading(false);
      setTimeout(() => {
        $("#dataTable").DataTable({
          pageLength: 5,
          destroy: true,
        });
      }, 100);
    } catch (error) {
      setError(error);
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    setLoading(true);
    fetchStudents(startDate, endDate); // These can be Date objects or strings
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setLoading(true);
    fetchStudents();
  };

  useEffect(() => {
    fetchStudents();

    return () => {
      if ($.fn.DataTable.isDataTable("#dataTable")) {
        $("#dataTable").DataTable().destroy(true);
      }
    };
  }, []);

  const handleDelete = (id) => {
    Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "This action will permanently delete the student.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        // const { token } = useSelector((state) => state.auth);

        axios
          .delete(`${baseUrl}/api/students/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then(() => {
            setStudents((prevStudents) =>
              prevStudents.filter((student) => student.id !== id)
            );
            fetchStudents();
            Swal.fire("Deleted!", "Student has been deleted.", "success");
          })
          .catch((error) => {
            console.error("Error deleting student:", error);
            const message =
              error.response?.data?.message ||
              error.message ||
              "Failed to delete student.";
            Swal.fire("Error", message, "error");
          });
      }
    });
  };

  const handleEdit = (studentData) => {
    navigate(`/edit-student/${studentData.id}`);
  };

  const columnHelper = createMRTColumnHelper();

  const columns = [
    columnHelper.accessor("firstName", {
      header: "Name",
      size: 30,
      Cell: (cell) =>
        cell.row.original?.firstName + " " + cell.row.original?.lastName,
    }),
    columnHelper.accessor("email", {
      header: "Email",
      size: 30,
    }),
    columnHelper.accessor("phone", {
      header: "Phone",
      size: 30,
      Cell: ({ row }) => {
        const phone = row.original?.phone;
        const countryCode = row.original?.country_code;
        return phone ? `${countryCode} ${phone}` : "-";
      },
    }),
    columnHelper.accessor("currentGrade", {
      header: "Current Grade",
      size: 30,
    }),
    columnHelper.accessor("schoolName", {
      header: "School Name",
      size: 30,
    }),
    columnHelper.accessor("created_at", {
      header: "Date",
      Cell: (cell) => {
        const dateStr = cell.row.original?.created_at;
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      },
    }),
    columnHelper.accessor((row) => `DDS000${row.id}`, {
      id: "studentId",
      header: "Student ID",
      size: 30,
    }),
  ];

  columns.unshift(
    columnHelper.accessor("Actions", {
      header: "Actions",
      size: 30,
      Cell: ({ cell }) => {
        const rowId = cell.row.original.id;
        const rowData = cell.row.original;

        return (
          <Box>
            <button
              onClick={() => handleEdit(rowData)}
              className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
            >
              <Icon icon="lucide:edit" />
            </button>
            <button
              onClick={() => handleDelete(rowId)}
              className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
              style={{ cursor: "pointer" }}
            >
              <Icon icon="mingcute:delete-2-line" />
            </button>
          </Box>
        );
      },
    })
  );

  const styles = {
    container: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
      padding: "16px",
      background: "#fff",
      borderRadius: "8px",
      marginBottom: "20px",
      alignItems: "flex-end",
    },
    group: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    label: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#333",
    },
    input: {
      padding: "6px 10px",
      fontSize: "14px",
      height: "36px",
      border: "1px solid #ccc",
      borderRadius: "6px",
      minWidth: "180px",
    },
    iconButton: {
      width: "36px",
      height: "36px",
      border: "none",
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      transition: "0.2s ease-in-out",
    },
    primary: {
      backgroundColor: "#e3f2fd",
      color: "#1976d2",
    },
    primaryHover: {
      backgroundColor: "#bbdefb",
    },
    secondary: {
      backgroundColor: "#fce4ec",
      color: "#d32f2f",
    },
    secondaryHover: {
      backgroundColor: "#f8bbd0",
    },
  };

  return (
    <>
      {/* Date Filter Section */}
      <div style={styles.container}>
        <div style={styles.group}>
          <label style={styles.label}>Start Date</label>
          <input
            type="date"
            value={startDate}
            max={endDate || ""}
            onChange={(e) => setStartDate(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.group}>
          <label style={styles.label}>End Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            style={styles.input}
          />
        </div>

        <button
          style={{
            ...styles.iconButton,
            ...styles.primary,
          }}
          onClick={handleDateFilter}
          disabled={loading}
          title="Apply Filter"
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor =
              styles.primaryHover.backgroundColor;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor =
              styles.primary.backgroundColor;
          }}
        >
          <Icon icon="material-symbols:search" />
        </button>

        <button
          style={{
            ...styles.iconButton,
            ...styles.secondary,
          }}
          onClick={handleClearFilter}
          disabled={loading}
          title="Clear Filter"
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor =
              styles.secondaryHover.backgroundColor;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor =
              styles.secondary.backgroundColor;
          }}
        >
          <Icon icon="material-symbols:cancel-outline-rounded" />
        </button>
      </div>
      <AdminCustomTable columns={columns} data={students} loading={loading} />
    </>
  );
};

export default AllStudent;
